import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type { FramesManager } from 'shared-libs/components/view/frames-manager'
import type {
  RadioButtonStyle,
  RadioOption,
  RadioOptionLayout,
  UI2RadioButtonFieldProps,
} from 'shared-libs/components/ui2/fields/ui2-radio-button-field/ui2-radio-button-field-props'
import {
  getRadioOptionColorTreatment,
  UI2_RADIO_BUTTON_FIELD_DEFAULT_PROPS,
} from 'shared-libs/components/ui2/fields/ui2-radio-button-field/ui2-radio-button-field-props'
import { createPulseFanout } from 'shared-libs/components/ui2/behavior/pulse-fanout'
import {
  getEffectiveTranslationTable,
  getOptionIdentity,
  getOptionLabel as translateOptionLabel,
  getStoredValueForOption,
  isOptionSelected,
  resolveSelectOptions,
} from 'shared-libs/components/ui2/fields/core-field/selectable-field-props'

import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import {
  IUI2PulseHighlightRef,
  UI2PulseHighlight,
} from 'browser/components/ui2/behavior/pulse-highlight/pulse-highlight'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'
import { IUI2FieldShellProps, UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'

import './_ui2-radio-button-field.scss'

const BULLET_PULSE_RADIUS = 999
const CHECKBOX_PULSE_RADIUS = 4
// Matches $ui2-radio-button-field-pill-radius — the ring has to trace the pill.
const PILL_PULSE_RADIUS = 8
const SEGMENTED_PULSE_RADIUS = 4

interface IUI2RadioWebExtras {
  onChange?: (value: any) => void
  className?: string
  style?: React.CSSProperties
  frames?: FramesManager
  errors?: any[]
}

/**
 * @uiComponent
 */
export interface IUI2RadioButtonFieldProps extends UI2RadioButtonFieldProps, IUI2RadioWebExtras {}

export class UI2RadioButtonField extends React.Component<IUI2RadioButtonFieldProps> {
  public static defaultProps: Partial<IUI2RadioButtonFieldProps> = {
    ...UI2_RADIO_BUTTON_FIELD_DEFAULT_PROPS,
  }

  // One ring per control, not one around the body — see `getShellPulseRef`.
  private optionPulseRefs: Record<number, React.RefObject<IUI2PulseHighlightRef>> = {}
  private groupPulseRef = React.createRef<IUI2PulseHighlightRef>()
  private optionPulseFanout = createPulseFanout(() =>
    Object.values(this.optionPulseRefs).map((ref) => ref.current),
  )

  public render() {
    const { className } = this.props
    const options = this.getTranslatedOptions()
    const buttonStyle = this.props.buttonStyle ?? 'default'
    const isMulti = !!this.props.isMultiSelect
    const isPill = buttonStyle === 'pill'
    const isSegmented = isPill && !isMulti && this.props.pillVariant !== 'standalone'

    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-radio-button-field', className)}
        renderReadOnly={(_, helpers) => this.renderReadOnly(options, helpers)}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
        pulseRef={this.getShellPulseRef(options, isSegmented)}
      >
        <UI2FieldControl
          className={classNames('ui2-radio-button-field__control', {
            'ui2-radio-button-field__control--segmented': isSegmented,
            'ui2-radio-button-field__control--default': !isPill,
            // Vertically, only the segmented grid keeps the control frame:
            // default rows are bullets on the form background and standalone
            // pills carry their own borders, so a second frame around either
            // read as a nested box. In horizontal layout every variant keeps
            // it — the body is a table cell there, and an unframed one breaks
            // the column of stacked fields.
            'ui2-radio-button-field__control--frameless':
              !isSegmented && !this.props.isHorizontalLayout,
          })}
        >
          {this.renderOptions(options, buttonStyle, isMulti, isSegmented)}
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  /**
   * The error/focus ring targets the controls themselves: each bubble or
   * checkbox, each standalone pill, or the segmented grid as a whole (it is
   * one control — ringing every cell reads as noise). Undefined with no
   * options, so the shell keeps its own body ring.
   */
  private getShellPulseRef(options: RadioOption[], isSegmented: boolean): any {
    if (_.isEmpty(options)) return undefined
    return isSegmented ? this.groupPulseRef : this.optionPulseFanout
  }

  private getOptionPulseRef(index: number): React.RefObject<IUI2PulseHighlightRef> {
    if (!this.optionPulseRefs[index]) {
      this.optionPulseRefs[index] = React.createRef<IUI2PulseHighlightRef>()
    }
    return this.optionPulseRefs[index]
  }

  private renderOptions(
    options: RadioOption[],
    buttonStyle: RadioButtonStyle,
    isMulti: boolean,
    isSegmented: boolean,
  ): React.ReactNode {
    const { isDisabled } = this.props
    if (_.isEmpty(options)) return null

    const layout = this.getOptionLayout()
    const isPill = buttonStyle === 'pill'

    const group = (
      <div
        className={classNames('ui2-radio-button-field__options', {
          'ui2-radio-button-field__options--horizontal': layout === 'horizontal',
          'ui2-radio-button-field__options--vertical': layout === 'vertical',
          'ui2-radio-button-field__options--pill': isPill,
          'ui2-radio-button-field__options--default': !isPill,
          'ui2-radio-button-field__options--segmented': isSegmented,
          'ui2-radio-button-field__options--disabled': isDisabled,
        })}
        role={isMulti ? 'group' : 'radiogroup'}
        aria-label={this.props.label || this.props.accessibilityLabel}
      >
        {options.map((option, index) =>
          this.renderOption(option, index, isPill, isMulti, isSegmented, layout),
        )}
      </div>
    )

    if (!isSegmented) return group
    return (
      <UI2PulseHighlight
        ref={this.groupPulseRef}
        className='ui2-radio-button-field__groupPulse'
        borderRadius={SEGMENTED_PULSE_RADIUS}
      >
        {group}
      </UI2PulseHighlight>
    )
  }

  private renderOption(
    option: RadioOption,
    index: number,
    isPill: boolean,
    isMulti: boolean,
    isSegmented: boolean,
    layout: RadioOptionLayout,
  ): React.ReactNode {
    const { testID, value, isMultiSelect } = this.props
    const isSelected = isOptionSelected(option, value, isMultiSelect)
    const isOptionDisabled = this.isOptionDisabled(option)
    const role = isMulti ? 'checkbox' : 'radio'
    // Segmented cells are equal-width and single-line; a second line there
    // would blow the row out, so help text is honored everywhere else.
    const helpText = isSegmented ? undefined : option.helpText

    if (isPill) {
      const optionIcon = !isSegmented ? option.icon : undefined
      const treatment = !isSegmented && isSelected
        ? getRadioOptionColorTreatment(option.selectedColorFamily)
        : undefined
      const pill = (
        <button
          key={index}
          type='button'
          role={role}
          aria-checked={isSelected}
          disabled={isOptionDisabled}
          onClick={() => this.handleToggle(option)}
          data-testid={testID ? `${testID}-option-${index}` : undefined}
          className={classNames('ui2-radio-button-field__pill', {
            'ui2-radio-button-field__pill--segmented': isSegmented,
            'ui2-radio-button-field__pill--standalone': !isSegmented,
            'ui2-radio-button-field__pill--selected': isSelected,
            'ui2-radio-button-field__pill--vertical': layout === 'vertical',
            'ui2-radio-button-field__pill--withHelp': !!helpText,
          })}
          // Semantic families resolve to a light fill with darker text, so the
          // icon and both text lines inherit via `color`.
          style={treatment
            ? { backgroundColor: treatment.background, color: treatment.foreground, borderColor: treatment.border }
            : undefined}
        >
          {!!optionIcon && (
            <UI2Icon name={optionIcon} size={14} className='ui2-radio-button-field__pill-icon' />
          )}
          <span className='ui2-radio-button-field__pill-text'>
            <span className='ui2-radio-button-field__pill-label'>{option.label}</span>
            {!!helpText && (
              <span className='ui2-radio-button-field__option-helpText'>{helpText}</span>
            )}
          </span>
        </button>
      )
      if (isSegmented) return pill
      return (
        <UI2PulseHighlight
          key={index}
          ref={this.getOptionPulseRef(index)}
          className='ui2-radio-button-field__optionPulse'
          borderRadius={PILL_PULSE_RADIUS}
        >
          {pill}
        </UI2PulseHighlight>
      )
    }

    return (
      <label
        key={index}
        className={classNames('ui2-radio-button-field__default', {
          'ui2-radio-button-field__default--selected': isSelected,
          'ui2-radio-button-field__default--disabled': isOptionDisabled,
        })}
      >
        <input
          type={isMulti ? 'checkbox' : 'radio'}
          checked={isSelected}
          disabled={isOptionDisabled}
          onChange={() => this.handleToggle(option)}
          className='ui2-radio-button-field__native-input'
          aria-label={option.label}
        />
        <UI2PulseHighlight
          ref={this.getOptionPulseRef(index)}
          className='ui2-radio-button-field__optionPulse'
          borderRadius={isMulti ? CHECKBOX_PULSE_RADIUS : BULLET_PULSE_RADIUS}
        >
          {isMulti ? this.renderCheckboxBox(isSelected) : this.renderBullet(isSelected)}
        </UI2PulseHighlight>
        <span className='ui2-radio-button-field__default-text'>
          <span className='ui2-radio-button-field__default-label'>{option.label}</span>
          {!!helpText && (
            <span className='ui2-radio-button-field__option-helpText'>{helpText}</span>
          )}
        </span>
      </label>
    )
  }

  private renderBullet(isSelected: boolean): React.ReactNode {
    return (
      <span
        className={classNames('ui2-radio-button-field__bullet', {
          'ui2-radio-button-field__bullet--selected': isSelected,
        })}
        aria-hidden='true'
      >
        {isSelected && <span className='ui2-radio-button-field__bullet-inner' />}
      </span>
    )
  }

  private renderCheckboxBox(isChecked: boolean): React.ReactNode {
    return (
      <span
        className={classNames('ui2-radio-button-field__checkbox', {
          'ui2-radio-button-field__checkbox--checked': isChecked,
        })}
        aria-hidden='true'
      >
        {isChecked && (
          <svg className='ui2-radio-button-field__checkmark' viewBox='0 0 16 16'>
            <polyline
              points='3.5,8.5 6.5,11.5 12.5,5.5'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )}
      </span>
    )
  }

  private renderReadOnly(options: RadioOption[], { editNode }: { editNode?: React.ReactNode } = {}): React.ReactNode {
    const { value, isMultiSelect } = this.props
    const labels = options
      .filter((o) => isOptionSelected(o, value, isMultiSelect))
      .map((o) => o.label)
    let content: React.ReactNode
    if (labels.length) {
      content = <span className='ui2-radio-button-field__read-only-text'>{labels.join(', ')}</span>
    } else if (!_.isNil(value) && String(value) !== '') {
      content = <span className='ui2-radio-button-field__read-only-text'>{String(value)}</span>
    } else {
      content = <UI2NoValueAvailable />
    }
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  private isOptionDisabled(option: RadioOption): boolean {
    return !!this.props.isDisabled || !!option.isDisabled
  }

  private handleToggle = (option: RadioOption): void => {
    const { isMultiSelect, value, onChange } = this.props
    if (this.isOptionDisabled(option) || !onChange) return

    const writable = getStoredValueForOption(option, this.props)
    if (isMultiSelect) {
      const current = _.isArray(value) ? value.slice() : []
      const optionId = getOptionIdentity(option.value)
      const existingIndex = current.findIndex(
        (v: any) => _.isEqual(getOptionIdentity(v), optionId),
      )
      if (existingIndex >= 0) {
        current.splice(existingIndex, 1)
      } else {
        current.push(writable)
      }
      onChange(current)
    } else {
      const isSame = _.isEqual(getOptionIdentity(value), getOptionIdentity(option.value))
      onChange(isSame ? undefined : writable)
    }
  }

  private getTranslatedOptions(): RadioOption[] {
    const translationTable = getEffectiveTranslationTable(this.props)
    return resolveSelectOptions(this.props).map((opt) => ({
      ...(opt as RadioOption),
      label: translateOptionLabel(opt, translationTable),
    }))
  }

  private getOptionLayout(): RadioOptionLayout {
    const { optionLayout, buttonStyle } = this.props
    if (optionLayout) return optionLayout
    return buttonStyle === 'pill' ? 'horizontal' : 'vertical'
  }
}
